import { useFadeSlideMotion } from '@/components/motion/use-fade-slide-motion';
import { MAZE_TIMER_LOW_WARNING_SECONDS } from '@/constants/maze';
import { APP_COLOR } from '@/theme/color';
import {
  isMazePlayModeDemo,
  isMazePlayModePreview,
  type MazePlayMode,
} from '@/utils/maze/maze-play-mode';
import { FlaskConical, Gamepad2, Settings, Volume2, VolumeX, Zap } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface MazeHudProps {
  readonly playMode: MazePlayMode;
  readonly level: number;
  readonly timer: number;
  readonly soundEffectsEnabled: boolean;
  readonly onToggleSoundEffects: (enabled: boolean) => void;
  readonly controlsEnabled: boolean;
  readonly onToggleControls: (enabled: boolean) => void;
  readonly onStartFresh: () => void | Promise<void>;
  readonly isStartFreshPending?: boolean;
  readonly onDone?: () => void;
  readonly tutorialUiDisabled?: boolean;
  readonly levelCompleteBurst?: boolean;
  /** When true, the level-flip transition is in progress (folding/unfolding). */
  readonly isLevelTransition?: boolean;
  readonly onSkip?: () => void;
  readonly isSkipPending?: boolean;
  /** When true, HUD metrics show pulse placeholders instead of store defaults. */
  readonly isMetricsLoading?: boolean;
  readonly stopAnimation?: boolean;
  readonly onStopAnimationToggle?: () => void;
}

export function TimerChipSkeleton() {
  return (
    <motion.div
      layout
      className="flex min-h-[2.25rem] min-w-0 items-center gap-2 rounded-full border px-3 py-2 backdrop-blur-md"
      style={{
        background: 'var(--maze-hud-pill-bg, rgba(255, 255, 255, 0.12))',
        borderColor: APP_COLOR.accentSoft30,
      }}
    >
      <div
        className="h-5 w-5 shrink-0 animate-pulse rounded-sm"
        style={{ backgroundColor: APP_COLOR.accentSoft40 }}
        aria-hidden
      />
      <div
        className="h-5 w-10 shrink-0 animate-pulse rounded-sm tabular-nums"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
        aria-hidden
      />
    </motion.div>
  );
}

export function LeftHudLabelSkeleton() {
  return (
    <div
      className="h-5 w-24 max-w-full animate-pulse rounded"
      style={{ backgroundColor: APP_COLOR.accentSoft20 }}
      aria-hidden
    />
  );
}

function CompactTimerChip({
  minutes,
  seconds,
  isTimerLowWarning,
  ariaLabel,
}: Readonly<{
  minutes: string;
  seconds: string;
  isTimerLowWarning: boolean;
  ariaLabel: string;
}>) {
  const urgentClass = isTimerLowWarning ? 'maze-timer-urgent' : '';

  return (
    <div
      className="flex min-w-0 items-center gap-2 rounded-full border p-[8px_18px] backdrop-blur-md"
      style={{
        borderColor: '#ffffff96',
        background: '#ffffff2e',
        color: 'white',
      }}
      aria-label={ariaLabel}
    >
      <svg
        className="timer-badge__icon h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        width="13"
        height="15"
        viewBox="0 0 13 15"
        fill="none"
        data-locator-target="vscode"
      >
        <path
          d="M6.44767 5.78274V8.44147L8.10938 9.4385M6.44767 2.79166C3.32736 2.79166 0.797852 5.32117 0.797852 8.44147C0.797852 11.5618 3.32736 14.0913 6.44767 14.0913C9.56797 14.0913 12.0975 11.5618 12.0975 8.44147C12.0975 5.32117 9.56797 2.79166 6.44767 2.79166ZM6.44767 2.79166V0.797607M5.1183 0.797607H7.77703M11.9838 3.18518L10.9868 2.18815L11.4853 2.68667M0.911526 3.18518L1.90855 2.18815L1.41004 2.68667"
          stroke="currentColor"
          strokeWidth="1.59524"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      <span
        className={`font-mono text-sm font-bold tabular-nums mt-[2px] ${isTimerLowWarning ? ` ${urgentClass}` : ''}`}
        style={{
          color: isTimerLowWarning ? APP_COLOR.danger : APP_COLOR.white,
        }}
      >
        {minutes}:{seconds}
      </span>
    </div>
  );
}

export function MazeHud({
  playMode,
  level,
  timer,
  soundEffectsEnabled,
  onToggleSoundEffects,
  controlsEnabled,
  onToggleControls,
  onStartFresh,
  isStartFreshPending = false,
  onDone,
  tutorialUiDisabled = false,
  levelCompleteBurst = false,
  isLevelTransition = false,
  isMetricsLoading = false,
  stopAnimation = false,
  onStopAnimationToggle,
  onSkip,
  isSkipPending = false,
}: Readonly<MazeHudProps>) {
  const { t } = useTranslation();
  const settingsMotion = useFadeSlideMotion();
  const isDemo = isMazePlayModeDemo(playMode);
  const isPreview = isMazePlayModePreview(playMode);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement | null>(null);
  const minutes = Math.floor(timer / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (timer % 60).toString().padStart(2, '0');
  const isTimerLowWarning = !isDemo && !isPreview && timer <= MAZE_TIMER_LOW_WARNING_SECONDS;

  useEffect(() => {
    if (!isSettingsOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!settingsRef.current?.contains(event.target as Node)) {
        setIsSettingsOpen(false);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isSettingsOpen]);

  const timerAria = t('hud.time');

  const renderLeftHudLabel = () => {
    if (isMetricsLoading) {
      return <LeftHudLabelSkeleton />;
    }
    if (levelCompleteBurst) {
      return (
        <span className="maze-hud-level font-semibold text-[16px] md:text-[18px] tracking-[1px] tracking-[0.16px] text-white whitespace-nowrap">
          {t('hud.levelCompleted')}
        </span>
      );
    }
    if (isPreview) {
      return (
        <span className="font-semibold text-[16px] md:text-[18px] tracking-[1px] tracking-[0.16px] text-white">
          {t('hud.preview')}
        </span>
      );
    }
    if (isDemo) {
      return (
        <span className="font-semibold text-[16px] md:text-[18px] tracking-[1px] tracking-[0.16px] text-white">
          {t('hud.demo')}
        </span>
      );
    }
    return (
      <span className="maze-hud-level font-semibold text-[16px] md:text-[18px] tracking-[1px] tracking-[0.16px] text-white">
        {t('hud.level')} {level}
      </span>
    );
  };

  return (
    <div className="relative z-20 mb-1 w-full max-w-md">
      <div className="relative flex justify-between w-full items-center gap-2">
        <div className="flex-1 min-w-0 justify-self-start pr-2">{renderLeftHudLabel()}</div>

        <div
          className="flex min-w-0 shrink-0 items-center justify-end justify-self-end gap-3"
          ref={settingsRef}
        >
          {isDemo && onDone ? (
            <button
              type="button"
              aria-disabled={tutorialUiDisabled}
              onClick={() => {
                if (tutorialUiDisabled) {
                  return;
                }
                onDone();
              }}
              className="rounded-full border px-4 py-2 text-[14px] font-semibold tracking-[0.16px] transition capitalize hover:!opacity-[0.8]"
              style={{
                borderColor: '#ffffff96',
                background: '#ffffff2e',
                color: 'white',
                opacity: tutorialUiDisabled ? 0.2 : 1,
                cursor: tutorialUiDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              {t('hud.done')}
            </button>
          ) : null}

          {!isDemo && !isPreview && (
            <button
              type="button"
              disabled={isSkipPending || levelCompleteBurst || isLevelTransition}
              onClick={onSkip}
              className={`mn-skip-btn${!levelCompleteBurst && !isLevelTransition && !isSkipPending ? '' : ' mn-skip-btn--hidden'}`}
            >
              {t('hud.skipLevel')}
            </button>
          )}

          <div className="justify-self-center empty:hidden">
            {!isDemo && !isPreview && isMetricsLoading ? <TimerChipSkeleton /> : null}
            {!isDemo && !isPreview && !isMetricsLoading ? (
              <CompactTimerChip
                minutes={minutes}
                seconds={seconds}
                isTimerLowWarning={isTimerLowWarning}
                ariaLabel={`${timerAria} ${minutes}:${seconds}`}
              />
            ) : null}
          </div>

          {!isPreview && (
            <>
              <button
                type="button"
                aria-label={t('hud.openSettings')}
                onClick={() => {
                  if (tutorialUiDisabled) {
                    return;
                  }
                  setIsSettingsOpen((open) => !open);
                }}
                className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border transition hover:!opacity-[0.8]"
                style={{
                  borderColor: '#ffffff96',
                  background: '#ffffff2e',
                  color: 'white',
                  opacity: tutorialUiDisabled ? 0.2 : 1,
                  cursor: tutorialUiDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <Settings className="h-5 w-5" />
              </button>

              <AnimatePresence>
                {isSettingsOpen ? (
                  <motion.div
                    key="maze-settings"
                    className="absolute right-0 top-full z-50 mt-3 border px-[18px] pb-[18px] pt-[16px]"
                    style={{
                      width: 230,
                      borderRadius: 14,
                      background: 'rgba(2, 16, 18, 0.97)',
                      borderColor: 'rgba(255, 255, 255, 0.13)',
                      boxShadow: `0 14px 44px rgba(0, 0, 0, 0.65), 0 0 0 1px ${APP_COLOR.frameShadow}`,
                    }}
                    {...settingsMotion}
                  >
                    {/* Decorative inner ring — mirrors arrows settings panel */}
                    <div
                      aria-hidden
                      style={{
                        position: 'absolute',
                        inset: 6,
                        border: `1px solid ${APP_COLOR.accentSoft30}`,
                        borderRadius: 10,
                        pointerEvents: 'none',
                      }}
                    />

                    <div className="relative mb-[14px]">
                      <p
                        className="text-[11px] font-bold uppercase tracking-[0.18em]"
                        style={{ color: APP_COLOR.accentBright }}
                      >
                        {t('hud.settings')}
                      </p>
                    </div>

                    <div className="relative space-y-4">
                      {/* Sound Effects */}
                      <button
                        type="button"
                        onClick={() => {
                          onToggleSoundEffects(!soundEffectsEnabled);
                        }}
                        className="flex w-full items-center justify-between text-left"
                      >
                        <span className="flex items-center gap-2">
                          {soundEffectsEnabled ? (
                            <Volume2
                              className="h-[14px] w-[14px] shrink-0"
                              style={{ color: APP_COLOR.accentBright }}
                            />
                          ) : (
                            <VolumeX
                              className="h-[14px] w-[14px] shrink-0"
                              style={{ color: APP_COLOR.accentBright }}
                            />
                          )}
                          <span className="text-[13px] font-semibold text-white">
                            {t('hud.soundEffects')}
                          </span>
                        </span>
                        <span className="flex items-center gap-2">
                          <span
                            className="text-[12px] font-bold"
                            style={{ color: APP_COLOR.accentSoft70 }}
                          >
                            {soundEffectsEnabled ? t('hud.soundOn') : t('hud.soundOff')}
                          </span>
                          <span
                            className="flex h-6 w-11 shrink-0 items-center rounded-full p-1 transition"
                            style={{
                              background: soundEffectsEnabled
                                ? APP_COLOR.accentSoft40
                                : APP_COLOR.whiteSoft10,
                              justifyContent: soundEffectsEnabled ? 'flex-end' : 'flex-start',
                            }}
                          >
                            <span
                              className="h-4 w-4 rounded-full"
                              style={{ background: APP_COLOR.white }}
                            />
                          </span>
                        </span>
                      </button>

                      {/* Controls */}
                      <button
                        type="button"
                        onClick={() => {
                          onToggleControls(!controlsEnabled);
                        }}
                        className="flex w-full items-center justify-between text-left"
                      >
                        <span className="flex items-center gap-2">
                          <Gamepad2
                            className="h-[14px] w-[14px] shrink-0"
                            style={{ color: APP_COLOR.accentBright }}
                          />
                          <span className="text-[13px] font-semibold text-white">
                            {t('hud.controls')}
                          </span>
                        </span>
                        <span className="flex items-center gap-2">
                          <span
                            className="text-[12px] font-bold"
                            style={{ color: APP_COLOR.accentSoft70 }}
                          >
                            {controlsEnabled ? t('hud.controlsOn') : t('hud.controlsOff')}
                          </span>
                          <span
                            className="flex h-6 w-11 shrink-0 items-center rounded-full p-1 transition"
                            style={{
                              background: controlsEnabled
                                ? APP_COLOR.accentSoft40
                                : APP_COLOR.whiteSoft10,
                              justifyContent: controlsEnabled ? 'flex-end' : 'flex-start',
                            }}
                          >
                            <span
                              className="h-4 w-4 rounded-full"
                              style={{ background: APP_COLOR.white }}
                            />
                          </span>
                        </span>
                      </button>

                      {/* Disable Animations */}
                      <button
                        type="button"
                        onClick={onStopAnimationToggle}
                        className="flex w-full items-center justify-between text-left"
                      >
                        <span className="flex items-center gap-2">
                          <Zap
                            className="h-[14px] w-[14px] shrink-0"
                            style={{ color: APP_COLOR.accentBright }}
                          />
                          <span className="text-[13px] font-semibold text-white">
                            {t('hud.disableAnimations')}
                          </span>
                        </span>
                        <span
                          className="flex h-6 w-11 shrink-0 items-center rounded-full p-1 transition"
                          style={{
                            background: stopAnimation
                              ? APP_COLOR.accentSoft40
                              : APP_COLOR.whiteSoft10,
                            justifyContent: stopAnimation ? 'flex-end' : 'flex-start',
                          }}
                        >
                          <span
                            className="h-4 w-4 rounded-full"
                            style={{ background: APP_COLOR.white }}
                          />
                        </span>
                      </button>

                      {/* Start Fresh */}
                      <div>
                        <button
                          type="button"
                          disabled={isStartFreshPending}
                          onClick={() => {
                            setIsSettingsOpen(false);
                            void onStartFresh();
                          }}
                          className="flex w-full items-center gap-2 whitespace-nowrap text-left disabled:cursor-wait disabled:opacity-70"
                        >
                          <FlaskConical
                            className="h-[14px] w-[14px] shrink-0"
                            style={{ color: APP_COLOR.accentBright }}
                          />
                          <span className="text-[13px] font-semibold text-white">
                            {t('hud.startFresh')}
                          </span>
                          <span
                            className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em]"
                            style={{
                              background: APP_COLOR.accentSoft30,
                              color: APP_COLOR.accentBright,
                            }}
                          >
                            {t('hud.testing')}
                          </span>
                        </button>
                        <p
                          className="mt-0.5 pl-[22px] text-[11px] uppercase tracking-[0.18em]"
                          style={{ color: APP_COLOR.accentSoft70 }}
                        >
                          {t('hud.returnHome')}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
